/**
 * Sykefraværskalkulator
 *
 * Beregner sykefraværsstatistikk i henhold til NAV og SSB-krav.
 *
 * ## Design Decisions
 *
 * ### DD-1: Sammenslåing av tilstøtende perioder
 * NAV krever at tilstøtende fraværsperioder slås sammen til ett "tilfelle".
 * Beslutning: Slår sammen alle typer (sickSelf, sickLeave, sickChildren) hvis tilstøtende.
 * Alternativ: Kunne holdt sickChildren separat - men NAV-veiledningen er uklar.
 *
 * ### DD-2: Grad ved sammenslåing
 * Når perioder med ulik grad slås sammen, bruker vi første dags grad for UI-visning.
 * Dette er en forenkling - dag-for-dag-data beholder faktisk grad per dag.
 * Alternativ: Kunne beregnet vektet gjennomsnitt.
 *
 * ### DD-3: Sykt barn i SSB-prosent
 * SSB ekskluderer omsorgsdager fra offisiell sykefraværsprosent.
 * absenceDays = ekskl. sykt barn (SSB), absenceDaysTotal = inkl. alt (forskrift).
 *
 * ### DD-4: Tilfelle telles i startkvartalet
 * NAV: "Hvert fravær registreres i det kvartalet fraværet starter."
 * En periode som starter i Q1 og varer til Q2 telles som 1 tilfelle i Q1, 0 i Q2.
 *
 * ### DD-5: Arbeidsdager uten arbeidsplan
 * Ansatte uten arbeidsplan får default arbeidsplan (08:00-16:00 ma-fr),
 * som matcher frontend sin defaultWorkSchedule.
 *
 * ### DD-6: Startår for statistikk
 * Fast startår 2018 for å matche eksisterende data.
 * TODO: Kunne vært konfigurerbart per organisasjon.
 */

import { sortBy } from 'lodash';
import { EmployeeViewModelGenderEnum } from '@trinnvis/dabih-api-client';
import { LocalDate } from 'src/utilities/local-date.js';
import type {
  AbsenceDay,
  AbsencePeriod,
  AbsenceType,
  AbsenceEmployee,
  DurationCategoryStats,
  EmployeeAbsenceData,
  HolidaysMinimal,
  WorkScheduleMinimal,
  WorkScheduleDayMinimal,
  WorkWeekMinimal,
} from './sickness-absence-types';
import { getDurationCategories } from './sickness-absence-types';
import type { EmployeeStatsPeriod } from '../pages/staffing-page/employee-stats-period';
import type { SicknessAbsenceStatsData } from '../pages/staffing-page/sickness-absence-stats-data';
import type { SicknessAbsenceStatsDataYear } from '../pages/staffing-page/sickness-absence-stats-data-year';
import type { SicknessAbsenceStatsNoGender } from '../pages/staffing-page/sickness-absence-stats-no-gender';
import type { QuarterData } from '../pages/staffing-page/quarter-data';
import type { StatisticsAggregate } from '../pages/staffing-page/statistics-aggregate';

// Re-export types for external use
export type { AbsenceEmployee, HolidaysMinimal } from './sickness-absence-types';

// ============================================================================
// Konfigurasjon
// ============================================================================

const FULL_DAY_HOURS = 7.5;
const STATISTICS_START_YEAR = 2018;

const DEFAULT_WORK_SCHEDULE: WorkScheduleMinimal = {
  start: '',
  holidaysAreTimeOff: true,
  workHoursDefinedPerDay: false,
  workDayStartTime: '08:00',
  workDayEndTime: '16:00',
  restPeriod: 'WITHOUT_PAY',
  workWeeks: [
    {
      monday: { workDay: true, start: '08:00', end: '16:00' },
      tuesday: { workDay: true, start: '08:00', end: '16:00' },
      wednesday: { workDay: true, start: '08:00', end: '16:00' },
      thursday: { workDay: true, start: '08:00', end: '16:00' },
      friday: { workDay: true, start: '08:00', end: '16:00' },
      saturday: { workDay: false },
      sunday: { workDay: false },
    },
  ],
};

// ============================================================================
// Hovedfunksjoner for fraværsberegning
// ============================================================================

/**
 * Bygger fraværsdata med dag-for-dag-detaljer for alle ansatte.
 * Slår sammen tilstøtende perioder til ett "tilfelle" per NAV-krav (DD-1).
 *
 * @param employees - Liste med ansatte (bruker AbsenceEmployee interface)
 * @param holidays - Helligdager som Record<date, true>
 * @param fullDayHours - Timer i en full arbeidsdag (default 7.5)
 * @returns Fraværsdata per ansatt med dag-for-dag-detaljer
 */
export function buildAbsencePeriods(
  employees: AbsenceEmployee[],
  holidays: HolidaysMinimal,
  fullDayHours: number = FULL_DAY_HOURS,
): EmployeeAbsenceData[] {
  if (!employees || employees.length === 0) {
    return [];
  }

  return employees.map((employee) => {
    const periods = buildPeriodsForEmployee(employee, holidays, fullDayHours);
    return {
      employeeUuid: employee.uuid,
      employeeName: employee.displayName,
      employeeGender: employee.gender ?? '',
      periods,
    };
  });
}

/**
 * Sjekker om gapet mellom to perioder kun inneholder ikke-arbeidsdager.
 * NAV: perioder uten "retur til arbeidslivet" regnes som ett tilfelle.
 * Hvis gapet kun er helger/helligdager/fridager iht. arbeidsplan, skal periodene slås sammen.
 */
function gapContainsNoWorkingDays(
  prevEnd: LocalDate,
  currentStart: LocalDate,
  employee: AbsenceEmployee,
  holidays: HolidaysMinimal,
): boolean {
  // Direkte tilstøtende (ingen gap)
  if (prevEnd.plusDays(1).isSame(currentStart)) {
    return true;
  }

  // Overlappende perioder
  if (prevEnd.isSameOrAfter(currentStart)) {
    return true;
  }

  // Sjekk alle dager i gapet (dagen etter prevEnd til dagen før currentStart)
  const gapDates = LocalDate.dates(prevEnd.plusDays(1), currentStart);
  return gapDates.every((date) => getWorkMinutesForDay(date, employee, holidays) === 0);
}

/**
 * Bygger fraværsperioder for én ansatt.
 * Håndterer edge cases: ingen perioder, ubekreftede perioder, tom arbeidsplan.
 */
function buildPeriodsForEmployee(
  employee: AbsenceEmployee,
  holidays: HolidaysMinimal,
  fullDayHours: number,
): AbsencePeriod[] {
  // Edge case: ingen fraværsperioder
  if (!employee.leavePeriods || employee.leavePeriods.length === 0) {
    return [];
  }

  // Filtrer kun bekreftede sykefraværsperioder
  const absenceTypes: AbsenceType[] = ['sickSelf', 'sickLeave', 'sickChildren'];
  const leavePeriods = employee.leavePeriods.filter((p) => absenceTypes.includes(p.type as AbsenceType) && p.confirmed);

  if (leavePeriods.length === 0) {
    return [];
  }

  // Sorter etter startdato
  const sortedPeriods = sortBy(leavePeriods, (p) => p.start);

  // Bygg perioder med dag-for-dag-data, slå sammen tilstøtende (DD-1)
  const result: AbsencePeriod[] = [];

  for (const leavePeriod of sortedPeriods) {
    // Edge case: ugyldig periode
    if (!leavePeriod.start || !leavePeriod.end) {
      continue;
    }

    const grade = leavePeriod.grade ?? 100;
    const absenceType = leavePeriod.type as AbsenceType;

    // Generer dager for denne perioden
    const days = generateDaysForPeriod(
      leavePeriod.start,
      leavePeriod.end,
      employee,
      holidays,
      fullDayHours,
      grade,
      absenceType,
    );

    // Sjekk om vi skal slå sammen med forrige periode (DD-1)
    if (result.length > 0) {
      const prevPeriod = result[result.length - 1];
      const prevEnd = LocalDate.fromString(prevPeriod.end);
      const currentStart = LocalDate.fromString(leavePeriod.start);

      // Tilstøtende = ingen arbeidsdager i gapet mellom periodene.
      // NAV: perioder uten "retur til arbeidslivet" regnes som ett tilfelle.
      // Dvs. helger/helligdager/fridager i gapet bryter ikke perioden.
      if (gapContainsNoWorkingDays(prevEnd, currentStart, employee, holidays)) {
        // Slå sammen
        prevPeriod.end = leavePeriod.end;
        prevPeriod.days.push(...days);
        prevPeriod.calendarDays =
          LocalDate.fromString(prevPeriod.start).until(LocalDate.fromString(prevPeriod.end)) + 1;
        prevPeriod.totalAbsenceDays = prevPeriod.days.reduce((sum, d) => sum + d.absenceDayValue, 0);
        if (absenceType === 'sickChildren') {
          prevPeriod.includesSickChildren = true;
        }
        if (!prevPeriod.absenceTypes.includes(absenceType)) {
          prevPeriod.absenceTypes.push(absenceType);
        }
        continue;
      }
    }

    // Ny periode
    const calendarDays = LocalDate.fromString(leavePeriod.start).until(LocalDate.fromString(leavePeriod.end)) + 1;
    const totalAbsenceDays = days.reduce((sum, d) => sum + d.absenceDayValue, 0);

    result.push({
      start: leavePeriod.start,
      end: leavePeriod.end,
      calendarDays,
      totalAbsenceDays,
      days,
      includesSickChildren: absenceType === 'sickChildren',
      absenceTypes: [absenceType],
    });
  }

  return result;
}

/**
 * Genererer dag-for-dag-data for en periode.
 * Tar hensyn til arbeidsplan, helligdager og ferier.
 */
function generateDaysForPeriod(
  start: string,
  end: string,
  employee: AbsenceEmployee,
  holidays: HolidaysMinimal,
  fullDayHours: number,
  grade: number,
  absenceType: AbsenceType,
): AbsenceDay[] {
  const days: AbsenceDay[] = [];
  const startDate = LocalDate.fromString(start);
  const endDate = LocalDate.fromString(end);

  // Edge case: ugyldig datointervall
  if (startDate.isAfter(endDate)) {
    return [];
  }

  const dates = LocalDate.dates(startDate, endDate.plusDays(1));

  for (const date of dates) {
    const workMinutes = getWorkMinutesForDay(date, employee, holidays);
    const workDayFraction = workMinutes / (fullDayHours * 60);

    if (workDayFraction > 0) {
      days.push({
        date: date.toString(),
        workDayFraction,
        sickLeaveGrade: grade,
        absenceType,
        absenceDayValue: workDayFraction * (grade / 100),
      });
    }
  }

  return days;
}

/**
 * Henter arbeidstid for en dag basert på ansattes arbeidsplan.
 * Returnerer 0 for ikke-arbeidsdager (DD-5).
 */
export function getWorkMinutesForDay(dayDate: LocalDate, employee: AbsenceEmployee, holidays: HolidaysMinimal): number {
  // Dager utenfor ansettelsesperioden er ikke arbeidsdager
  if (employee.employmentStartDate && dayDate.isBefore(LocalDate.fromString(employee.employmentStartDate))) {
    return 0;
  }
  if (employee.employmentEndDate && dayDate.isAfter(LocalDate.fromString(employee.employmentEndDate))) {
    return 0;
  }

  const day = dayDate.toString();

  // Sjekk helligdag
  if (holidays[day] !== undefined) {
    const schedule = getCurrentSchedule(dayDate, employee);
    if (schedule.holidaysAreTimeOff) {
      return 0;
    }
  }

  // Sjekk ferie
  const onVacation = employee.leavePeriods?.some(
    (p) =>
      p.type === 'vacation' &&
      p.confirmed &&
      LocalDate.fromString(p.start).isSameOrBefore(dayDate) &&
      LocalDate.fromString(p.end).isSameOrAfter(dayDate),
  );
  if (onVacation) {
    return 0;
  }

  // Sjekk arbeidsplan-unntak
  if (employee.workScheduleExceptions) {
    for (const exception of employee.workScheduleExceptions) {
      if (dayDate.isSame(LocalDate.fromString(exception.date))) {
        if (!exception.work) {
          return 0;
        }
        if (exception.start && exception.end) {
          return durationInMinutes(exception.start, exception.end);
        }
      }
    }
  }

  // Hent fra arbeidsplan (DD-5: bruker default 08:00-16:00 ma-fr hvis ingen plan)
  const schedule = getCurrentSchedule(dayDate, employee);
  if (!schedule.workWeeks?.length) {
    return 0;
  }

  const weekDayOrdinal = dayDate.weekDayOrdinal();
  const weekIndex = getWeekIndex(dayDate, schedule);
  const week = schedule.workWeeks[weekIndex];

  if (!week) {
    return 0;
  }

  const scheduleDay = forDayIndex(week, weekDayOrdinal);

  if (!scheduleDay?.workDay) {
    return 0;
  }

  let minutes = 0;
  if (!schedule.workHoursDefinedPerDay && schedule.workDayStartTime && schedule.workDayEndTime) {
    minutes = durationInMinutes(schedule.workDayStartTime, schedule.workDayEndTime);
  } else if (scheduleDay.start && scheduleDay.end) {
    minutes = durationInMinutes(scheduleDay.start, scheduleDay.end);
  }

  // Trekk fra 30 min ubetalt lunsj (matcher calculateScheduledWorkDay)
  if (schedule.restPeriod === 'WITHOUT_PAY' && minutes >= 5 * 60) {
    minutes -= 30;
  }

  return minutes;
}

function getCurrentSchedule(dayDate: LocalDate, employee: AbsenceEmployee): WorkScheduleMinimal {
  if (!employee.workSchedules || employee.workSchedules.length === 0) {
    return DEFAULT_WORK_SCHEDULE;
  }

  const schedules = sortBy(employee.workSchedules, (s) => s.start).reverse();
  for (const schedule of schedules) {
    if (!schedule.start || LocalDate.fromString(schedule.start).isSameOrBefore(dayDate)) {
      return schedule;
    }
  }
  return DEFAULT_WORK_SCHEDULE;
}

function getWeekIndex(dayDate: LocalDate, schedule: WorkScheduleMinimal): number {
  if (!schedule.workWeeks || schedule.workWeeks.length === 0) {
    return 0;
  }

  const weeksInSchedule = schedule.workWeeks.length;
  if (weeksInSchedule === 1) {
    return 0;
  }

  if (!schedule.start) {
    return 0;
  }

  const startDate = LocalDate.fromString(schedule.start);
  const startDateWeekStart = startDate.minusDays(startDate.weekDayOrdinal());
  const thisDayWeekStart = dayDate.minusDays(dayDate.weekDayOrdinal());
  const daysDiff = startDateWeekStart.until(thisDayWeekStart);

  let weekCount = 1;
  if (daysDiff > 6) {
    weekCount = Math.floor(daysDiff / 7) + 1;
  }

  let theRightWeek = weekCount % weeksInSchedule;
  if (theRightWeek === 0) {
    theRightWeek = weeksInSchedule;
  }

  return theRightWeek - 1;
}

function forDayIndex(week: WorkWeekMinimal, dayOrdinal: number): WorkScheduleDayMinimal | undefined {
  const days: (keyof WorkWeekMinimal)[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  return week[days[dayOrdinal]];
}

function durationInMinutes(start: string, end: string): number {
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);

  // Edge case: ugyldige tidspunkter
  if (isNaN(startH) || isNaN(startM) || isNaN(endH) || isNaN(endM)) {
    return 0;
  }

  const result = endH * 60 + endM - (startH * 60 + startM);
  return result > 0 ? result : 0;
}

// ============================================================================
// Kvartalsfunksjoner
// ============================================================================

/**
 * Beregner fraværsdager som faller innenfor et kvartal for en periode.
 * Bruker dag-for-dag-data for presis splitting over kvartalgrenser.
 */
export function getAbsenceDaysInQuarter(period: AbsencePeriod, quarterStart: string, quarterEnd: string): number {
  if (!period.days || period.days.length === 0) {
    return 0;
  }

  const qStart = LocalDate.fromString(quarterStart);
  const qEnd = LocalDate.fromString(quarterEnd);

  return period.days
    .filter((day) => {
      const d = LocalDate.fromString(day.date);
      return d.isSameOrAfter(qStart) && d.isSameOrBefore(qEnd);
    })
    .reduce((sum, day) => sum + day.absenceDayValue, 0);
}

/**
 * Teller antall tilfeller (perioder) som STARTER i et kvartal (DD-4).
 * NAV: "Hvert fravær telles som ett tilfelle og registreres bare i det kvartalet fraværet starter i."
 */
export function getCaseCountInQuarter(
  employeeData: EmployeeAbsenceData[],
  quarterStart: string,
  quarterEnd: string,
  filter?: { excludeSickChildren?: boolean; absenceType?: AbsenceType },
): number {
  if (!employeeData || employeeData.length === 0) {
    return 0;
  }

  const qStart = LocalDate.fromString(quarterStart);
  const qEnd = LocalDate.fromString(quarterEnd);

  let count = 0;
  for (const employee of employeeData) {
    if (!employee.periods) continue;

    for (const period of employee.periods) {
      const periodStart = LocalDate.fromString(period.start);

      // Sjekk om perioden starter i kvartalet
      if (!periodStart.isSameOrAfter(qStart) || !periodStart.isSameOrBefore(qEnd)) {
        continue;
      }

      // Filtrer på type om nødvendig
      if (filter?.excludeSickChildren && period.includesSickChildren) {
        continue;
      }
      if (filter?.absenceType && !period.absenceTypes.includes(filter.absenceType)) {
        continue;
      }

      count++;
    }
  }

  return count;
}

/**
 * Aggregerer fraværsdata per varighetskategori for et kvartal.
 * Returnerer statistikk for alle kategorier inkludert egenmeldt/legemeldt-splitten.
 */
export function aggregateByDurationCategory(
  employeeData: EmployeeAbsenceData[],
  quarterStart: string,
  quarterEnd: string,
): DurationCategoryStats {
  const result: DurationCategoryStats = {
    short: { caseCount: 0, absenceDaysInQuarter: 0 },
    shortSickSelf: { caseCount: 0, absenceDaysInQuarter: 0 },
    shortSickLeave: { caseCount: 0, absenceDaysInQuarter: 0 },
    medium: { caseCount: 0, absenceDaysInQuarter: 0 },
    mediumSickSelf: { caseCount: 0, absenceDaysInQuarter: 0 },
    mediumSickLeave: { caseCount: 0, absenceDaysInQuarter: 0 },
    long: { caseCount: 0, absenceDaysInQuarter: 0 },
    extraLong: { caseCount: 0, absenceDaysInQuarter: 0 },
    sickChildren: { caseCount: 0, absenceDaysInQuarter: 0 },
  };

  if (!employeeData || employeeData.length === 0) {
    return result;
  }

  const qStart = LocalDate.fromString(quarterStart);
  const qEnd = LocalDate.fromString(quarterEnd);

  for (const employee of employeeData) {
    if (!employee.periods) continue;

    for (const period of employee.periods) {
      const periodStart = LocalDate.fromString(period.start);

      // Perioden må starte i kvartalet for å telle som tilfelle (DD-4)
      const startsInQuarter = periodStart.isSameOrAfter(qStart) && periodStart.isSameOrBefore(qEnd);

      // Beregn dager som faller i kvartalet
      const daysInQuarter = getAbsenceDaysInQuarter(period, quarterStart, quarterEnd);

      if (daysInQuarter === 0 && !startsInQuarter) {
        continue;
      }

      // Sykt barn er separat (DD-3)
      if (period.includesSickChildren) {
        if (startsInQuarter) {
          result.sickChildren.caseCount++;
        }
        result.sickChildren.absenceDaysInQuarter += daysInQuarter;
        continue;
      }

      // Finn varighetskategorier basert på TOTAL periodelengde
      const categories = getDurationCategories(period.calendarDays);

      // Bestem om det er egenmeldt eller legemeldt
      const isSickSelf = period.absenceTypes.includes('sickSelf');
      const isSickLeave = period.absenceTypes.includes('sickLeave');

      for (const category of categories) {
        if (category === 'short') {
          if (startsInQuarter) {
            result.short.caseCount++;
            if (isSickSelf) result.shortSickSelf.caseCount++;
            if (isSickLeave) result.shortSickLeave.caseCount++;
          }
          result.short.absenceDaysInQuarter += daysInQuarter;
          if (isSickSelf) result.shortSickSelf.absenceDaysInQuarter += daysInQuarter;
          if (isSickLeave) result.shortSickLeave.absenceDaysInQuarter += daysInQuarter;
        } else if (category === 'medium') {
          if (startsInQuarter) {
            result.medium.caseCount++;
            if (isSickSelf) result.mediumSickSelf.caseCount++;
            if (isSickLeave) result.mediumSickLeave.caseCount++;
          }
          result.medium.absenceDaysInQuarter += daysInQuarter;
          if (isSickSelf) result.mediumSickSelf.absenceDaysInQuarter += daysInQuarter;
          if (isSickLeave) result.mediumSickLeave.absenceDaysInQuarter += daysInQuarter;
        } else if (category === 'long') {
          if (startsInQuarter) {
            result.long.caseCount++;
          }
          result.long.absenceDaysInQuarter += daysInQuarter;
        } else if (category === 'extraLong') {
          if (startsInQuarter) {
            result.extraLong.caseCount++;
          }
          result.extraLong.absenceDaysInQuarter += daysInQuarter;
        }
      }
    }
  }

  return result;
}

// ============================================================================
// UI-kompatible funksjoner for sykefraværsstatistikk
// ============================================================================

/**
 * Beregner arbeidsdager for en ansatt i en gitt periode.
 * Eksportert for testing.
 */
export function calculateWorkDaysForEmployee(
  employee: AbsenceEmployee,
  startDate: LocalDate,
  endDate: LocalDate,
  holidays: HolidaysMinimal,
  fullDayHours: number = FULL_DAY_HOURS,
): number {
  // Edge case: ugyldig datointervall
  if (startDate.isAfter(endDate)) {
    return 0;
  }

  const dates = LocalDate.dates(startDate, endDate.plusDays(1));
  let totalMinutes = 0;

  for (const date of dates) {
    totalMinutes += getWorkMinutesForDay(date, employee, holidays);
  }

  return totalMinutes / (60 * fullDayHours);
}

/**
 * Konverterer AbsencePeriod til EmployeeStatsPeriod for UI-kompatibilitet.
 * Bruker første dags grad for forenkling (DD-2).
 */
export function toEmployeeStatsPeriod(
  period: AbsencePeriod,
  employeeData: EmployeeAbsenceData,
  quarterStart: string,
  quarterEnd: string,
): EmployeeStatsPeriod {
  const absenceDaysInQuarter = getAbsenceDaysInQuarter(period, quarterStart, quarterEnd);

  // Finn grad (DD-2: bruker første dag for forenkling)
  const grade = period.days.length > 0 ? period.days[0].sickLeaveGrade : 100;

  return {
    employeeUuid: employeeData.employeeUuid,
    employeeName: employeeData.employeeName,
    employeeGender: employeeData.employeeGender,
    types: period.includesSickChildren ? ['sickChildren'] : period.absenceTypes,
    start: period.start,
    end: period.end,
    absenceDays: period.totalAbsenceDays,
    absenceDaysInQuarter,
    grade,
  };
}

/**
 * Bygger statistikk-aggregat for et kjønn i et kvartal.
 * Eksportert for testing.
 */
export function buildGenderAggregate(
  employees: AbsenceEmployee[],
  absenceData: EmployeeAbsenceData[],
  quarterStart: string,
  quarterEnd: string,
  holidays: HolidaysMinimal,
  gender: string,
  fullDayHours: number = FULL_DAY_HOURS,
): StatisticsAggregate {
  const startDate = LocalDate.fromString(quarterStart);
  const endDate = LocalDate.fromString(quarterEnd);

  // Filtrer ansatte etter kjønn
  const genderEmployees = employees.filter((e) => e.gender === gender);

  // Beregn arbeidsdager
  let workDays = 0;
  for (const employee of genderEmployees) {
    workDays += calculateWorkDaysForEmployee(employee, startDate, endDate, holidays, fullDayHours);
  }

  // Finn perioder som starter i kvartalet for dette kjønnet
  const periods: EmployeeStatsPeriod[] = [];
  let absenceDays = 0;
  let periodsCountTotal = 0;

  for (const employeeAbsence of absenceData) {
    if (employeeAbsence.employeeGender !== gender) continue;
    if (!employeeAbsence.periods) continue;

    for (const period of employeeAbsence.periods) {
      const periodStart = LocalDate.fromString(period.start);

      // Kun perioder som starter i kvartalet (DD-4)
      if (!periodStart.isSameOrAfter(startDate) || !periodStart.isSameOrBefore(endDate)) {
        continue;
      }

      // Ekskluder sykt barn fra absenceDays og periodsCountTotal (DD-3: SSB-metodikk)
      if (!period.includesSickChildren) {
        absenceDays += getAbsenceDaysInQuarter(period, quarterStart, quarterEnd);
        periodsCountTotal++;
      }

      periods.push(toEmployeeStatsPeriod(period, employeeAbsence, quarterStart, quarterEnd));
    }
  }

  return {
    workDays,
    absenceDays,
    periodsCountTotal,
    periods,
  };
}

/**
 * Bygger kvartalsdata for UI.
 * Eksportert for testing.
 */
export function buildQuarterData(
  employees: AbsenceEmployee[],
  absenceData: EmployeeAbsenceData[],
  quarterStart: string,
  quarterEnd: string,
  holidays: HolidaysMinimal,
  quarterName: string,
  fullDayHours: number = FULL_DAY_HOURS,
): QuarterData {
  const startDate = LocalDate.fromString(quarterStart);
  const endDate = LocalDate.fromString(quarterEnd);

  // Beregn totale arbeidsdager
  let workDays = 0;
  for (const employee of employees) {
    workDays += calculateWorkDaysForEmployee(employee, startDate, endDate, holidays, fullDayHours);
  }

  // Beregn fraværsdager (DD-3)
  let absenceDays = 0; // Uten sykt barn (SSB)
  let absenceDaysTotal = 0; // Med sykt barn (forskrift)

  for (const employeeAbsence of absenceData) {
    if (!employeeAbsence.periods) continue;

    for (const period of employeeAbsence.periods) {
      const periodStart = LocalDate.fromString(period.start);

      // Kun perioder som starter i kvartalet (DD-4)
      if (!periodStart.isSameOrAfter(startDate) || !periodStart.isSameOrBefore(endDate)) {
        continue;
      }

      const daysInQuarter = getAbsenceDaysInQuarter(period, quarterStart, quarterEnd);
      absenceDaysTotal += daysInQuarter;

      if (!period.includesSickChildren) {
        absenceDays += daysInQuarter;
      }
    }
  }

  return {
    quarterName,
    workDays,
    absenceDays,
    absenceDaysTotal,
    female: buildGenderAggregate(
      employees,
      absenceData,
      quarterStart,
      quarterEnd,
      holidays,
      EmployeeViewModelGenderEnum.Female,
      fullDayHours,
    ),
    male: buildGenderAggregate(
      employees,
      absenceData,
      quarterStart,
      quarterEnd,
      holidays,
      EmployeeViewModelGenderEnum.Male,
      fullDayHours,
    ),
  };
}

/**
 * Bygger årsdata for UI.
 * Eksportert for testing.
 */
export function buildYearData(
  employees: AbsenceEmployee[],
  absenceData: EmployeeAbsenceData[],
  year: string,
  holidays: HolidaysMinimal,
  fullDayHours: number = FULL_DAY_HOURS,
): SicknessAbsenceStatsDataYear {
  const yearStart = LocalDate.fromString(`${year}-01-01`);
  const yearEnd = LocalDate.fromString(`${year}-12-31`);

  // Beregn totale arbeidsdager for året
  let workDays = 0;
  for (const employee of employees) {
    workDays += calculateWorkDaysForEmployee(employee, yearStart, yearEnd, holidays, fullDayHours);
  }

  // Beregn fraværsdager for året (DD-3)
  let absenceDays = 0;
  let absenceDaysTotal = 0;

  for (const employeeAbsence of absenceData) {
    if (!employeeAbsence.periods) continue;

    for (const period of employeeAbsence.periods) {
      const periodStart = LocalDate.fromString(period.start);

      // Kun perioder som starter i dette året
      if (!periodStart.isSameOrAfter(yearStart) || !periodStart.isSameOrBefore(yearEnd)) {
        continue;
      }

      const daysInYear = getAbsenceDaysInQuarter(period, yearStart.toString(), yearEnd.toString());
      absenceDaysTotal += daysInYear;

      if (!period.includesSickChildren) {
        absenceDays += daysInYear;
      }
    }
  }

  // Bygg kvartalene
  const quarters: QuarterData[] = [];
  for (let q = 0; q < 4; q++) {
    const quarterStart = yearStart.plusMonths(q * 3);
    const quarterEnd = quarterStart.plusMonths(3).minusDays(1);

    quarters.push(
      buildQuarterData(
        employees,
        absenceData,
        quarterStart.toString(),
        quarterEnd.toString(),
        holidays,
        `${q + 1}. kvartal `,
        fullDayHours,
      ),
    );
  }

  // Bygg kjønnsaggregater for året
  const female = buildGenderAggregate(
    employees,
    absenceData,
    yearStart.toString(),
    yearEnd.toString(),
    holidays,
    EmployeeViewModelGenderEnum.Female,
    fullDayHours,
  );

  const male = buildGenderAggregate(
    employees,
    absenceData,
    yearStart.toString(),
    yearEnd.toString(),
    holidays,
    EmployeeViewModelGenderEnum.Male,
    fullDayHours,
  );

  return {
    yearName: year,
    workDays,
    absenceDays,
    absenceDaysTotal,
    female,
    male,
    quarters,
  };
}

/**
 * Beregner sykefraværsstatistikk med eksplisitte parametre.
 */
export function calculateSicknessAbsenceStatsFromData(
  employees: AbsenceEmployee[],
  holidays: HolidaysMinimal,
  currentYear: number,
  fullDayHours: number = FULL_DAY_HOURS,
): SicknessAbsenceStatsData | SicknessAbsenceStatsNoGender {
  // Edge case: ingen ansatte
  if (!employees || employees.length === 0) {
    return {
      genderDefined: true,
      years: [],
    };
  }

  // Sjekk om alle ansatte har definert kjønn
  const genderDefinedForAll = employees.every(
    (e) => e.gender !== '' && e.gender !== EmployeeViewModelGenderEnum.Undefined,
  );

  if (!genderDefinedForAll) {
    return { genderDefined: false };
  }

  // Bygg fraværsdata med dag-for-dag-detaljer
  const absenceData = buildAbsencePeriods(employees, holidays, fullDayHours);

  // Bygg årsdata fra 2018 til nåværende år (DD-6)
  const years: SicknessAbsenceStatsDataYear[] = [];

  for (let year = STATISTICS_START_YEAR; year <= currentYear; year++) {
    years.push(buildYearData(employees, absenceData, year.toString(), holidays, fullDayHours));
  }

  return {
    genderDefined: true,
    years,
  };
}
